// Logique du dépôt au guichet : étapes du parcours (les mêmes que le portail), lecture de la
// version publiée d'une démarche (formulaire, pièces, règles, identité), pièces attendues selon
// les réponses, règles d'éligibilité évaluées avec le moteur partagé.
import {
  evaluerCondition,
  schemaContenuVersion,
  schemaFormulaire,
  type Condition,
  type ContenuVersion,
  type DonneesFormulaire,
  type Formulaire,
} from '@cc/contrats';

export const ETAPES_GUICHET = ['demarche', 'usager', 'formulaire', 'pieces', 'relecture'] as const;
export type EtapeGuichet = (typeof ETAPES_GUICHET)[number];

export const LIBELLES_ETAPES_GUICHET: Record<EtapeGuichet, string> = {
  demarche: 'Démarche',
  usager: 'Usager',
  formulaire: 'Formulaire',
  pieces: 'Pièces',
  relecture: 'Relecture et dépôt',
};

export const LIBELLES_PIECE_IDENTITE: Record<string, string> = {
  carte_nationale_identite: "Carte nationale d'identité",
  passeport: 'Passeport',
  titre_sejour: 'Titre de séjour',
  permis_conduire: 'Permis de conduire',
  acte_naissance: 'Acte de naissance',
  autre: 'Autre pièce',
};

export interface UsagerRecherche {
  id: string;
  identifiant: string;
  nom: string;
  prenoms: string | null;
  identite_verifiee: {
    le: string;
    type_piece: string;
    date_piece: string;
    collectivite: string | null;
  } | null;
}

export interface PieceDeposee {
  document_id: string;
  type: string;
  titre: string;
}

export interface SaisieGuichet {
  /** Identifiant local du brouillon, jamais envoyé au serveur. */
  capture_id?: string;
  demarche_id: string;
  demarche_version_id: string;
  mode_usager: 'compte' | 'tiers';
  usager: UsagerRecherche | null;
  tiers_nom: string;
  telephone: string;
  courriel: string;
  service_id: string;
  donnees: DonneesFormulaire;
}

export const SAISIE_VIDE: SaisieGuichet = {
  demarche_id: '',
  demarche_version_id: '',
  mode_usager: 'compte',
  usager: null,
  tiers_nom: '',
  telephone: '',
  courriel: '',
  service_id: '',
  donnees: {},
};

/** Description d'une version utile au guichet ; `formulaire` est nul hors format de l'éditeur. */
export interface VersionGuichet {
  formulaire: Formulaire | null;
  pieces: ContenuVersion['pieces'];
  regles: ContenuVersion['conditions_eligibilite']['regles'];
  texteConditions: string;
  identiteVerifieeRequise: boolean;
  tarifs: Record<string, number>;
  /** Champs d'un formulaire de l'ancien format (démarche interne de test). */
  champsSimples: { code: string; libelle: string; obligatoire: boolean }[];
}

export function lireVersionGuichet(
  contenu: Record<string, unknown> | undefined,
): VersionGuichet | null {
  if (!contenu) return null;
  const r = schemaContenuVersion.safeParse(contenu);
  if (r.success) {
    const t = r.data.tarif;
    return {
      formulaire: r.data.formulaire,
      pieces: r.data.pieces,
      regles: r.data.conditions_eligibilite.regles,
      texteConditions: r.data.conditions_eligibilite.texte,
      identiteVerifieeRequise: r.data.identite_verifiee_requise,
      tarifs:
        t.rubrique && typeof t.montant_indicatif === 'number'
          ? { [t.rubrique]: t.montant_indicatif }
          : {},
      champsSimples: [],
    };
  }
  const f = contenu.formulaire as
    | { sections?: unknown; champs?: { code: string; libelle: string; obligatoire?: boolean }[] }
    | undefined;
  const formulaire = schemaFormulaire.safeParse(f);
  const pieces = Array.isArray(contenu.pieces) ? (contenu.pieces as ContenuVersion['pieces']) : [];
  return {
    formulaire: formulaire.success && formulaire.data.sections.length > 0 ? formulaire.data : null,
    pieces: pieces.map((p) => ({
      type: p.type,
      libelle: p.libelle,
      obligatoire: p.obligatoire !== false,
    })),
    regles: [],
    texteConditions: '',
    identiteVerifieeRequise: Boolean(contenu.identite_verifiee_requise),
    tarifs: {},
    champsSimples: (f?.champs ?? []).map((c) => ({
      code: c.code,
      libelle: c.libelle,
      obligatoire: Boolean(c.obligatoire),
    })),
  };
}

/** Pièces demandées pour ces réponses (les pièces conditionnelles dont la condition n'est pas remplie sont écartées). */
export function piecesAttendues(v: VersionGuichet, donnees: DonneesFormulaire) {
  return v.pieces.filter((p) => evaluerCondition(p.condition as Condition | undefined, donnees));
}

/** Messages des règles d'éligibilité non remplies. */
export function reglesNonRemplies(v: VersionGuichet, donnees: DonneesFormulaire): string[] {
  return v.regles.filter((r) => !evaluerCondition(r.condition, donnees)).map((r) => r.message);
}

/** Pièces obligatoires attendues sans document fourni. */
export function piecesManquantes(
  v: VersionGuichet,
  donnees: DonneesFormulaire,
  fournies: PieceDeposee[],
): string[] {
  const types = new Set(fournies.map((p) => p.type));
  return piecesAttendues(v, donnees)
    .filter((p) => p.obligatoire && !types.has(p.type))
    .map((p) => p.libelle);
}

export function etapeSuivante(e: EtapeGuichet): EtapeGuichet {
  return ETAPES_GUICHET[
    Math.min(ETAPES_GUICHET.indexOf(e) + 1, ETAPES_GUICHET.length - 1)
  ] as EtapeGuichet;
}

export function etapePrecedente(e: EtapeGuichet): EtapeGuichet {
  return ETAPES_GUICHET[Math.max(ETAPES_GUICHET.indexOf(e) - 1, 0)] as EtapeGuichet;
}
